// Declared-view addressing for headless process renders (plan
// 2026-07-07-bpmn-lanes-pools P5). `arch_render viewName=…`, the server's
// view route, and the CLI's `--view-name` all resolve a `flow` view here:
// instance → parent merge, knob bindings, flow-ref (path or `$knob`),
// mode dispatch, and — for `bpmn` — the lane plan evaluated over the
// rendered layout's performers. The live flow tab resolves through the same
// helper so a knob-target view renders identically live and headless.

import {
  assignBpmnLanes,
  bpmnLayout,
  buildNodeUniverse,
  performersOf,
  processIdentity,
  resolveBpmnLanes,
  resolveKnobBindings,
  resolveViewInstance,
  type BpmnLaneLint,
  type BpmnLanePlan,
  type BpmnLayout,
  type EvalContext,
  type FlowBodyClause,
  type ProcessModel,
  type QualifiedName,
  type QueryDecl,
  type ResolvedModel,
  type SourceSpan,
  type Value,
  type ViewDeclV2,
} from "@archlang/engine";
import { renderProcessSvg } from "@archlang/scene/process";
import { BPMN_EXPORT_OPTIONS, freshBpmnScaffold, renderProcessSwimlaneSvg } from "./process-render.js";
import { buildReplicateOption, replicateKeyOf, buildEngineOption, engineKeyOf } from "./process-replicate.js";
import { lookupDeclaredView } from "./view-lookup.js";

export type FlowViewMode = "flow" | "sequence" | "bpmn";

export interface ResolvedFlowView {
  /** The effective view (instance merged onto its parent when applicable). */
  readonly view: ViewDeclV2;
  readonly mode: FlowViewMode;
  readonly proc: ProcessModel;
  /** The `flow … bpmn { … }` lane/pool body, when declared. */
  readonly body?: readonly FlowBodyClause[];
  readonly knobs: ReadonlyMap<string, Value>;
}

export type FlowViewResolution =
  | { readonly ok: true; readonly resolved: ResolvedFlowView }
  | { readonly ok: false; readonly error: string };

/** The flow-ref resolution rule shared with the live flow tab. A
 *  module-scoped view resolves in its module context FIRST — the owner chain
 *  walked outward — then falls back to the global qualified-suffix match
 *  (the `ViewDeclV2.ownerPath` scope contract; without it a bare `Checkout`
 *  in a B-scoped view would doc-order-pick A's same-named process). */
export function resolveFlowProcess(
  model: ResolvedModel,
  dotted: string,
  scopePath?: QualifiedName,
  // v0.11 (D3): the REFERRER's landed space — a spaced view's bare flow ref
  // prefers its own space's process over a cross-space twin.
  scopeSpace?: QualifiedName,
): ProcessModel | null {
  const procs = model.processModels ?? [];
  const qualifiedOf = (p: ProcessModel) => [...(p.ownerPath ?? []), p.name].join(".");
  const sameSpace = (p: ProcessModel) =>
    (p.space ?? []).join(".") === (scopeSpace ?? []).join(".");
  // Internal-handle forms first (VIEWS.md §intro — the synthesized process-tab
  // view addresses its subject by `processKey`, id before name): a stable #id,
  // else the content-keyed identity the wire model mints for an ANONYMOUS run
  // (`processIdentity`), paired back to its IR by the same source-position key
  // the serializer uses.
  const byId = procs.find((p) => p.id === dotted);
  if (byId) return byId;
  // v0.11 (D2): a spaced no-`#id` process's wire identity is
  // `Space::Qualified.Name` — match it before the bare-name arms.
  if (dotted.includes("::")) {
    const spaced = procs.find(
      (p) => p.space && `${p.space.join(".")}::${qualifiedOf(p)}` === dotted,
    );
    if (spaced) return spaced;
  }
  if (dotted.includes("(anon:")) {
    const decl = (model.processes ?? []).find((p) => processIdentity(p) === dotted);
    const s = decl?.source;
    if (!s) return null;
    return (
      procs.find(
        (p) => p.source && p.source.file === s.file && p.source.start.offset === s.start.offset,
      ) ?? null
    );
  }
  if (scopePath && scopePath.length > 0) {
    for (let i = scopePath.length; i > 0; i--) {
      const want = [...scopePath.slice(0, i), dotted].join(".");
      const hits = procs.filter((p) => !p.anonymous && p.name && qualifiedOf(p) === want);
      const hit = hits.find(sameSpace) ?? hits[0];
      if (hit) return hit;
    }
  }
  const path = dotted.split(".");
  const name = path[path.length - 1];
  const matches = procs.filter((p) => {
    if (p.name !== name) return false;
    const qualified = qualifiedOf(p);
    return path.length === 1 || qualified === dotted || qualified.endsWith("." + dotted);
  });
  return matches.find(sameSpace) ?? matches[0] ?? null;
}

/**
 * Resolve a declared view by name into its flow-render inputs. Errors are
 * user-facing strings (the MCP/server/CLI error surface): unknown view, a
 * non-flow representation, an unbound `$target`, an unresolvable process.
 */
export function resolveFlowViewByName(model: ResolvedModel, name: string): FlowViewResolution {
  const views = model.views ?? [];
  // Addressing = the shared resolver doctrine (`lookupDeclaredView`):
  // space-qualified spelling first (v0.11), then unique qualified path, then
  // unique bare name; twins refuse with the unambiguous spellings.
  const looked = lookupDeclaredView(model, name);
  if (!looked.ok) return looked;
  const raw = looked.view;
  // Instance-parent lookup prefers the instance's own scope, then the
  // first-declared global (deterministic).
  const scopeKey = (raw.ownerPath ?? []).join(".");
  const byName = new Map<string, ViewDeclV2>();
  for (const v of views) if (!byName.has(v.name)) byName.set(v.name, v);
  for (const v of views) if ((v.ownerPath ?? []).join(".") === scopeKey) byName.set(v.name, v);
  const { view } = resolveViewInstance(raw, byName);
  const fc = view.clauses.find((c) => c.kind === "flow");
  if (!fc) {
    return {
      ok: false,
      error: `view "${name}" has no \`flow\` clause — render a table/matrix/grid view, or use \`view: board|bpmn|flow|sequence\` + \`process:\` for the direct surfaces`,
    };
  }
  const universe = buildNodeUniverse(model);
  const { knobs } = resolveKnobBindings(view, universe);
  let dotted: string;
  if (fc.ref.kind === "ref") {
    dotted = fc.ref.path.join(".");
  } else {
    const bound = knobs.get(fc.ref.name);
    if (bound?.kind === "identifier") dotted = bound.text;
    else if (bound?.kind === "ref") dotted = bound.path.join(".");
    else {
      return {
        ok: false,
        error: `view "${name}": flow target \`$${fc.ref.name}\` is unbound — render a view INSTANCE that binds it`,
      };
    }
  }
  const proc = resolveFlowProcess(model, dotted, view.ownerPath, view.space);
  if (!proc) {
    return { ok: false, error: `view "${name}": flow target "${dotted}" resolves to no process` };
  }
  const mode: FlowViewMode = fc.mode ?? "flow";
  return {
    ok: true,
    resolved: { view, mode, proc, ...(fc.body ? { body: fc.body } : {}), knobs },
  };
}

/** The lane-selector evaluation context for a resolved flow view. Named-query
 *  atoms need the workspace's lexical query closure, which lives in the
 *  SOURCES (app-shell's `collectViewGovernance`), not on `ResolvedModel` —
 *  callers with that closure pass it via `queries`; the headless path
 *  resolves universe + knob atoms (a query atom then reads as an unknown
 *  ref — empty set, same as every other unresolved ref). */
function laneEvalCtx(
  model: ResolvedModel,
  resolved: ResolvedFlowView,
  queries?: ReadonlyMap<string, QueryDecl>,
): EvalContext {
  return {
    universe: buildNodeUniverse(model),
    knobs: resolved.knobs as Map<string, Value>,
    ...(queries ? { queries } : {}),
    ...(resolved.view.ownerPath ? { scopePath: resolved.view.ownerPath } : {}),
  };
}

/** Layout + lane plan for a resolved bpmn flow view (shared live/headless). */
export function bpmnLayoutForFlowView(
  model: ResolvedModel,
  resolved: ResolvedFlowView,
  expand?: "all" | readonly string[],
  queries?: ReadonlyMap<string, QueryDecl>,
): { layout: BpmnLayout; plan: BpmnLanePlan | null } {
  // `replicate @@<key>` → a per-step system cylinder beside each consuming
  // step (aspect render mode `replicated`), resolved from the model's places.
  const key = replicateKeyOf(resolved.body);
  const replicate = key ? buildReplicateOption(model, key) : undefined;
  // `engine @@<key>` → a caller-derived dangling aspect badge on each step,
  // orthogonal to `replicate` (both may appear in one view).
  const engKey = engineKeyOf(resolved.body);
  const engine = engKey ? buildEngineOption(model, engKey) : undefined;
  const base = bpmnLayout(freshBpmnScaffold(model, resolved.proc, expand), {
    ...(replicate ? { replicate } : {}),
    ...(engine ? { engine } : {}),
  });
  if (!resolved.body || resolved.body.length === 0) return { layout: base, plan: null };
  const plan = resolveBpmnLanes(
    resolved.body,
    performersOf(base),
    laneEvalCtx(model, resolved, queries),
  );
  return { layout: assignBpmnLanes(base, plan), plan };
}

/** One resolved lane-plan lint, anchored to a source span for a diagnostic. */
export interface AnchoredBpmnLint {
  /** Qualified name of the offending `flow … bpmn` view. */
  readonly viewName: string;
  readonly lint: BpmnLaneLint;
  /** `lint.source` when the lint pins a clause; else the `flow` clause span
   *  (the `pools-only` body-shape lint carries no clause of its own). */
  readonly span?: SourceSpan;
}

/**
 * Semantic lane-plan lints for every declared `flow … bpmn { … }` view (plan
 * 2026-07-07-bpmn-lanes-pools §7 — the LSP diagnostics surface).
 *
 * Each view is walked FRESH-OPEN (`expand: "all"`) so the lane plan runs
 * against the splice-inclusive participation set: a performer buried in a
 * subprocess that the live board happens to render collapsed must not trip the
 * empty-lane lint. Views that fail to resolve (unbound `$target`, no process,
 * non-bpmn mode) contribute nothing — their own diagnostics cover them.
 */
export function computeBpmnViewLints(
  model: ResolvedModel,
  queries?: ReadonlyMap<string, QueryDecl>,
): AnchoredBpmnLint[] {
  const out: AnchoredBpmnLint[] = [];
  for (const v of model.views ?? []) {
    const viewName = [...(v.ownerPath ?? []), v.name].join(".");
    const r = resolveFlowViewByName(model, viewName);
    if (!r.ok || r.resolved.mode !== "bpmn") continue;
    if (!r.resolved.body || r.resolved.body.length === 0) continue;
    const flowSpan = r.resolved.view.clauses.find((c) => c.kind === "flow")?.source;
    const { plan } = bpmnLayoutForFlowView(model, r.resolved, "all", queries);
    for (const lint of plan?.lints ?? []) {
      const span = lint.source ?? flowSpan;
      out.push({ viewName, lint, ...(span ? { span } : {}) });
    }
  }
  return out;
}

/** Render a declared flow view by name → portable SVG (or an error string). */
export function renderFlowViewSvg(
  model: ResolvedModel,
  viewName: string,
  expand?: "all" | readonly string[],
): { readonly svg: string } | { readonly error: string } {
  const r = resolveFlowViewByName(model, viewName);
  if (!r.ok) return { error: r.error };
  if (r.resolved.mode === "bpmn") {
    const { layout } = bpmnLayoutForFlowView(model, r.resolved, expand);
    return { svg: renderProcessSvg(layout, BPMN_EXPORT_OPTIONS) };
  }
  const variant = r.resolved.mode === "sequence" ? "sequence" : "flow";
  return { svg: renderProcessSwimlaneSvg(model, r.resolved.proc, variant, expand) };
}
